import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  TouchableOpacity,
  ActivityIndicator,
  SafeAreaView,
  useWindowDimensions,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Colors } from '@/constants/colors';
import { StockHeader } from './components/StockHeader';
import { IntelligentOutlookTab } from './components/IntelligentOutlookTab';
import { ChartTab } from './components/ChartTab';
import { ChatTab } from './components/ChatTab';
import { polygonMarketDataService } from '@/services/api/polygonMarketData.service';
import { useWatchlist } from '@/hooks/useWatchlist';
import { getStockSearchSummary } from '@/services/history/searchHistoryService';

type TabType = 'outlook' | 'chart' | 'chat';

interface StockItem {
  ticker: string;
  companyName: string;
  isWatchlisted?: boolean;
}

interface StockAnalysisScreenProps {
  route: {
    params: {
      ticker?: string;
      companyName?: string;
      initialTab?: TabType;
      initialQuery?: string; // User's search query for chat tab
      stocks?: StockItem[]; // Optional: pass custom stock list
      sourceType?: 'watchlist' | 'recent' | 'custom'; // Where the stocks come from
    };
  };
  navigation: any;
  hideHeader?: boolean; // Optional: hide header when embedded (default: false)
  hideStocksList?: boolean; // Optional: hide stocks list panel when embedded (default: false)
}

export const StockAnalysisScreen: React.FC<StockAnalysisScreenProps> = ({
  route,
  navigation,
  hideHeader = false,
  hideStocksList = false,
}) => {
  const {
    ticker: initialTicker,
    companyName: initialCompanyName,
    initialTab = 'outlook',
    initialQuery,
    stocks: passedStocks,
    sourceType = 'recent',
  } = route.params || {};

  const [selectedTicker, setSelectedTicker] = useState(initialTicker || '');
  const [selectedCompany, setSelectedCompany] = useState(initialCompanyName || '');
  const [activeTab, setActiveTab] = useState<TabType>(initialTab);
  const [stocksList, setStocksList] = useState<StockItem[]>(passedStocks || []);
  const [stocksLoading, setStocksLoading] = useState(!passedStocks);

  const [stockPrice, setStockPrice] = useState<number | undefined>(undefined);
  const [priceChange, setPriceChange] = useState<number | undefined>(undefined);
  const [priceChangePercent, setPriceChangePercent] = useState<number | undefined>(undefined);
  const [priceLoading, setPriceLoading] = useState(true);

  const { watchlist, addToWatchlist, removeFromWatchlist } = useWatchlist();
  const { width } = useWindowDimensions();
  const isLargeScreen = width >= 768;

  // Load stocks if not passed
  useEffect(() => {
    if (passedStocks) {
      setStocksLoading(false);
      return;
    }

    const loadStocks = async () => {
      try {
        setStocksLoading(true);

        if (sourceType === 'watchlist') {
          // Use watchlist stocks
          setStocksList(
            watchlist.map(item => ({
              ticker: item.ticker,
              companyName: item.companyName,
              isWatchlisted: true,
            }))
          );
        } else {
          // Load recent searches
          const summaryResult = await getStockSearchSummary();
          if (summaryResult.success && summaryResult.summaries) {
            const stocks = summaryResult.summaries.map(summary => ({
              ticker: summary.stock_symbol,
              companyName: summary.stock_name,
              isWatchlisted: watchlist.some(w => w.ticker === summary.stock_symbol),
            }));
            setStocksList(stocks);
          }
        }

        setStocksLoading(false);
      } catch (error) {
        console.error('[StockAnalysisScreen] Error loading stocks:', error);
        setStocksLoading(false);
      }
    };

    loadStocks();
  }, [sourceType, passedStocks, watchlist]);

  // Set initial selection if not set
  useEffect(() => {
    if (!selectedTicker && stocksList.length > 0) {
      setSelectedTicker(stocksList[0].ticker);
      setSelectedCompany(stocksList[0].companyName);
    }
  }, [stocksList, selectedTicker]);

  // Fetch live stock price
  useEffect(() => {
    if (!selectedTicker) return;

    const fetchPrice = async () => {
      try {
        setPriceLoading(true);
        console.log(`[StockAnalysisScreen] Fetching price for ${selectedTicker}`);

        const { data, error } = await polygonMarketDataService.getQuote(selectedTicker);

        if (error || !data) {
          console.error(`[StockAnalysisScreen] Failed to fetch price:`, error);
          setPriceLoading(false);
          return;
        }

        setStockPrice(data.price);
        setPriceChange(data.change);
        setPriceChangePercent(data.changePercent);
      } catch (err) {
        console.error(`[StockAnalysisScreen] Error fetching price:`, err);
      } finally {
        setPriceLoading(false);
      }
    };

    fetchPrice();
  }, [selectedTicker]);

  const handleStockSelect = (ticker: string, companyName: string) => {
    setSelectedTicker(ticker);
    setSelectedCompany(companyName);
    setPriceLoading(true);
  };

  const handleToggleWatchlist = async () => {
    const isCurrentlyWatchlisted = watchlist.some(item => item.ticker === selectedTicker);

    if (isCurrentlyWatchlisted) {
      await removeFromWatchlist(selectedTicker);
    } else {
      await addToWatchlist(selectedTicker, selectedCompany);
    }

    // Update stocksList to reflect watchlist change
    setStocksList(prev =>
      prev.map(stock =>
        stock.ticker === selectedTicker
          ? { ...stock, isWatchlisted: !isCurrentlyWatchlisted }
          : stock
      )
    );
  };

  const renderTabContent = () => {
    if (!selectedTicker) return null;

    switch (activeTab) {
      case 'outlook':
        return <IntelligentOutlookTab ticker={selectedTicker} companyName={selectedCompany} />;
      case 'chart':
        return <ChartTab ticker={selectedTicker} />;
      case 'chat':
        return (
          <ChatTab
            ticker={selectedTicker}
            companyName={selectedCompany}
            initialQuery={initialQuery}
          />
        );
      default:
        return null;
    }
  };

  const renderStockItem = ({ item }: { item: StockItem }) => {
    const isSelected = selectedTicker === item.ticker;

    return (
      <TouchableOpacity
        style={[styles.stockItem, isSelected && styles.selectedStockItem]}
        onPress={() => handleStockSelect(item.ticker, item.companyName)}
      >
        <View style={styles.stockItemLeft}>
          <Text style={styles.stockItemTicker}>{item.ticker}</Text>
          <Text style={styles.stockItemCompany} numberOfLines={1}>
            {item.companyName}
          </Text>
        </View>
        {item.isWatchlisted && <Ionicons name="star" size={16} color={Colors.accent.primary} />}
      </TouchableOpacity>
    );
  };

  const leftPanel = (
    <View style={styles.leftPanel}>
      <View style={styles.leftPanelHeader}>
        <Text style={styles.leftPanelTitle}>
          {sourceType === 'watchlist' ? 'Watchlist' : 'Recent Stocks'}
        </Text>
        <Text style={styles.leftPanelCount}>{stocksList.length} stocks</Text>
      </View>

      {stocksLoading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={Colors.primary} />
        </View>
      ) : stocksList.length === 0 ? (
        <View style={styles.emptyContainer}>
          <Ionicons name="trending-up-outline" size={64} color={Colors.text.tertiary} />
          <Text style={styles.emptyTitle}>No stocks yet</Text>
          <Text style={styles.emptyText}>
            {sourceType === 'watchlist'
              ? 'Add stocks to your watchlist to see them here'
              : 'Search for stocks to see recent activity'}
          </Text>
        </View>
      ) : (
        <FlatList
          data={stocksList}
          renderItem={renderStockItem}
          keyExtractor={item => item.ticker}
          ItemSeparatorComponent={() => <View style={styles.stockSeparator} />}
        />
      )}
    </View>
  );

  const detailPanel = (
    <View style={styles.rightPanel}>
      {selectedTicker ? (
        <>
          {/* Stock Header with Watchlist Button */}
          <View style={styles.headerContainer}>
            <View style={styles.headerLeft}>
              <StockHeader
                ticker={selectedTicker}
                companyName={selectedCompany}
                price={stockPrice}
                change={priceChange}
                changePercent={priceChangePercent}
                isLoading={priceLoading}
              />
            </View>
            <TouchableOpacity style={styles.watchlistButton} onPress={handleToggleWatchlist}>
              <Ionicons
                name={
                  watchlist.some(item => item.ticker === selectedTicker) ? 'star' : 'star-outline'
                }
                size={24}
                color={Colors.accent.primary}
              />
            </TouchableOpacity>
          </View>

          {/* Tab Bar */}
          <View style={styles.tabBar}>
            <TouchableOpacity
              style={[styles.tab, activeTab === 'outlook' && styles.activeTab]}
              onPress={() => setActiveTab('outlook')}
            >
              <Ionicons
                name="analytics"
                size={20}
                color={activeTab === 'outlook' ? Colors.primary : Colors.text.tertiary}
              />
              <Text style={[styles.tabText, activeTab === 'outlook' && styles.activeTabText]}>
                Outlook
              </Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[styles.tab, activeTab === 'chart' && styles.activeTab]}
              onPress={() => setActiveTab('chart')}
            >
              <Ionicons
                name="bar-chart"
                size={20}
                color={activeTab === 'chart' ? Colors.primary : Colors.text.tertiary}
              />
              <Text style={[styles.tabText, activeTab === 'chart' && styles.activeTabText]}>
                Chart
              </Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[styles.tab, activeTab === 'chat' && styles.activeTab]}
              onPress={() => setActiveTab('chat')}
            >
              <Ionicons
                name="chatbubbles"
                size={20}
                color={activeTab === 'chat' ? Colors.primary : Colors.text.tertiary}
              />
              <Text style={[styles.tabText, activeTab === 'chat' && styles.activeTabText]}>
                Chat
              </Text>
            </TouchableOpacity>
          </View>

          {/* Tab Content */}
          <View style={styles.tabContent}>{renderTabContent()}</View>
        </>
      ) : (
        <View style={styles.emptyDetailPanel}>
          <Ionicons name="trending-up" size={64} color={Colors.text.tertiary} />
          <Text style={styles.emptyDetailText}>Select a stock to view analysis</Text>
        </View>
      )}
    </View>
  );

  return (
    <SafeAreaView style={styles.container}>
      {/* Header */}
      {!hideHeader && (
        <View style={styles.header}>
          <TouchableOpacity style={styles.backButton} onPress={() => navigation.goBack()}>
            <Ionicons name="chevron-back" size={24} color={Colors.text.primary} />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>Stock Analysis</Text>
          <View style={styles.headerRight} />
        </View>
      )}

      {/* Content */}
      {isLargeScreen && !hideStocksList ? (
        <View style={styles.splitContainer}>
          {leftPanel}
          <View style={styles.divider} />
          {detailPanel}
        </View>
      ) : (
        detailPanel
      )}
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.background.primary,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: Colors.border.subtle,
  },
  backButton: {
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'flex-start',
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: Colors.text.primary,
  },
  headerRight: {
    width: 40,
  },
  splitContainer: {
    flex: 1,
    flexDirection: 'row',
  },
  leftPanel: {
    width: 300,
    borderRightWidth: 1,
    borderRightColor: Colors.border.default,
    backgroundColor: Colors.background.primary,
  },
  leftPanelHeader: {
    paddingHorizontal: 16,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: Colors.border.subtle,
  },
  leftPanelTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: Colors.text.primary,
    marginBottom: 4,
  },
  leftPanelCount: {
    fontSize: 12,
    color: Colors.text.secondary,
  },
  stockItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 14,
    backgroundColor: Colors.background.primary,
  },
  selectedStockItem: {
    backgroundColor: Colors.background.secondary,
    borderLeftWidth: 3,
    borderLeftColor: Colors.primary,
  },
  stockItemLeft: {
    flex: 1,
  },
  stockItemTicker: {
    fontSize: 15,
    fontWeight: '700',
    color: Colors.text.primary,
    marginBottom: 2,
  },
  stockItemCompany: {
    fontSize: 12,
    color: Colors.text.secondary,
  },
  stockSeparator: {
    height: 1,
    backgroundColor: Colors.border.subtle,
    marginLeft: 16,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 32,
  },
  emptyTitle: {
    marginTop: 16,
    fontSize: 18,
    fontWeight: '600',
    color: Colors.text.primary,
  },
  emptyText: {
    marginTop: 8,
    fontSize: 14,
    color: Colors.text.secondary,
    textAlign: 'center',
  },
  divider: {
    width: 1,
    backgroundColor: Colors.border.default,
  },
  rightPanel: {
    flex: 1,
    backgroundColor: Colors.background.primary,
  },
  headerContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingRight: 16,
  },
  headerLeft: {
    flex: 1,
  },
  watchlistButton: {
    padding: 8,
  },
  tabBar: {
    flexDirection: 'row',
    backgroundColor: Colors.background.primary,
    borderBottomWidth: 1,
    borderBottomColor: Colors.border.subtle,
  },
  tab: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    gap: 6,
    borderBottomWidth: 2,
    borderBottomColor: 'transparent',
  },
  activeTab: {
    borderBottomColor: Colors.primary,
  },
  tabText: {
    fontSize: 14,
    fontWeight: '500',
    color: Colors.text.tertiary,
  },
  activeTabText: {
    color: Colors.primary,
    fontWeight: '600',
  },
  tabContent: {
    flex: 1,
  },
  emptyDetailPanel: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 40,
  },
  emptyDetailText: {
    fontSize: 16,
    color: Colors.text.secondary,
    marginTop: 16,
    textAlign: 'center',
  },
});
